/**
 * Local Saves Domain
 * The save manager's saves of a world on the local server: the save of the
 * world as it is now, the list of the saves there — or the server's English
 * when it cannot be read — and a save's delete on its way, or why it failed.
 * A world is made from a save in the portal, not here.
 *
 * A save goes to the match through the command gate (devtools.go) and is on
 * its way (`localSave`) until the server answers — saved to its folder, said
 * in a toast, or refused with the server's English, which the save manager
 * shows under its own title — or the socket drops with it, or it never went,
 * kept as the gate's reason. The save manager awaits how it ended, and closes
 * on a save. The list and a delete are RPCs over HTTP (local-saves.go), which
 * refuse as errors: callRpcHttp throws the refusal, in the server's English,
 * and an answer is the call done — it carries no verdict to read.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MessageType, OpCode } from '../constants/op-codes';
import i18n from '../i18n/config';
import { callRpcHttp } from '../services/nakama/http-rpc';
import { sendGatedCommand, type NotSentReason } from './command-gate';
import { showSuccessToast } from './toasts';

// ==================== TYPES ====================

/**
 * LocalSaveInfo represents a saved world state on the server filesystem
 */
export interface LocalSaveInfo {
  save_id: string; // e.g., "000", "001"
  world_name: string; // Original world name when saved
  created_at: number; // Unix timestamp
  game_ticks: number; // Game ticks at time of save
  team_count: number; // Number of teams in the save
}

/**
 * The save of the world: none on its way, on its way, refused with the
 * server's words, or never sent — kept as the gate's reason, worded when the
 * save manager shows it. A save that went through is a toast, and the save
 * manager closes.
 */
type LocalSave =
  | { state: 'idle' }
  | { state: 'saving' }
  | { state: 'failed'; error: string }
  | { state: 'notSent'; reason: NotSentReason };

/** How a save ended, for the save manager that asked: saved, refused or never sent, or gone with the connection. */
type LocalSaveOutcome = 'saved' | 'failed' | 'dropped';

/** What list_local_saves answers: the saves, an empty list for none. */
interface ListLocalSavesAnswer {
  saves: LocalSaveInfo[];
}

// ==================== STORE ====================

/**
 * LocalSaves state interface
 */
interface LocalSavesState {
  // State
  localSave: LocalSave;
  saves: LocalSaveInfo[];
  isLoading: boolean;
  /** Why the list could not be read, in the server's or the browser's English. */
  error: string | null;
  /** The save whose delete is on its way. */
  deleting: string | null;
  /** Why the last delete failed, in the server's English. */
  deleteError: string | null;

  // Actions
  setSaves: (saves: LocalSaveInfo[]) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  /** A failed save said before goes: the save manager is opening afresh. A save on its way stays. */
  clearLocalSaveFailure: () => void;
  /** As the player leaves a match: a save on its way ends, dropped, and none is said; the list and a delete's state go too. */
  reset: () => void;
}

/**
 * Initial state
 */
const initialState: Pick<LocalSavesState, 'localSave' | 'saves' | 'isLoading' | 'error' | 'deleting' | 'deleteError'> = {
  localSave: { state: 'idle' },
  saves: [],
  isLoading: false,
  error: null,
  deleting: null,
  deleteError: null,
};

/**
 * The save manager's store: the save on its way, the list of saves, and a
 * delete on its way.
 */
export const useLocalSavesStore = create<LocalSavesState>()(
  devtools(
    (set) => ({
      ...initialState,

      setSaves: (saves: LocalSaveInfo[]) =>
        set({ saves }, false, 'localSaves/setSaves'),

      setLoading: (isLoading: boolean) =>
        set({ isLoading }, false, 'localSaves/setLoading'),

      setError: (error: string | null) =>
        set({ error }, false, 'localSaves/setError'),

      clearLocalSaveFailure: () =>
        set(
          (state) => (state.localSave.state === 'failed' || state.localSave.state === 'notSent' ? { localSave: { state: 'idle' } } : state),
          false,
          'localSaves/clearLocalSaveFailure',
        ),

      reset: () => {
        settle('dropped');
        set(initialState, false, 'localSaves/reset');
      },
    }),
    { name: 'LocalSavesStore' }
  )
);

// ==================== HANDLERS ====================

/** Settles the save on its way with how it ended, once: its answer, a failed send, or the gate's end. */
let settleLocalSave: ((outcome: LocalSaveOutcome) => void) | null = null;

function settle(outcome: LocalSaveOutcome): void {
  const resolve = settleLocalSave;
  settleLocalSave = null;
  resolve?.(outcome);
}

/**
 * The world saved to its folder (OpCode 904, to the admin who asked): said in
 * a toast, as the save manager closes.
 */
export function handleDevSaveStressLocalSuccess(data: { save_id: string; message: string }): void {
  console.log('[LocalSaves] Local save written:', data.save_id, data.message);
  useLocalSavesStore.setState({ localSave: { state: 'idle' } }, false, 'localSaves/saved');
  showSuccessToast(i18n.t('saveManager.saveSuccess', { id: data.save_id }));
  settle('saved');
}

/** The save refused (OpCode 905, to the admin who asked): the server's English, under the save manager's own title. */
export function handleDevSaveStressLocalFailed(data: { error: string }): void {
  console.warn('[LocalSaves] The server refused the local save:', data.error);
  useLocalSavesStore.setState({ localSave: { state: 'failed', error: data.error } }, false, 'localSaves/saveFailed');
  settle('failed');
}

// ==================== THE PLAYER'S ACTS ====================

/** The save manager opens afresh: the last save's failure, said on its last opening, goes. */
export function clearLocalSaveFailure(): void {
  useLocalSavesStore.getState().clearLocalSaveFailure();
}

// ==================== SERVICE METHODS ====================

/**
 * Asks the server to save the world as it is now to a numbered folder of the
 * local saves — or to overwrite the folder `saveId` names — and resolves with
 * how it ended. One at a time: a second ask while one is on its way is
 * dropped.
 */
export function requestLocalStressSave(saveId?: string): Promise<LocalSaveOutcome> {
  if (useLocalSavesStore.getState().localSave.state === 'saving') return Promise.resolve('dropped');
  useLocalSavesStore.setState({ localSave: { state: 'saving' } }, false, 'localSaves/saving');
  const outcome = new Promise<LocalSaveOutcome>((resolve) => {
    settleLocalSave = resolve;
  });

  const message = { type: MessageType.MessageTypeDevSaveStressLocal, ...(saveId ? { save_id: saveId } : {}) };
  void sendGatedCommand(OpCode.OpCodeDevSaveStressLocal, JSON.stringify(message), {
    // The answer will not come: the gate turned the save away, or the socket
    // went with it, or the player left.
    endWait: () => {
      if (useLocalSavesStore.getState().localSave.state === 'saving') {
        useLocalSavesStore.setState({ localSave: { state: 'idle' } }, false, 'localSaves/saveDropped');
      }
      settle('dropped');
    },
    // Never sent: kept as the gate's reason, and said under the save manager's
    // title as a refusal is, in the language the player reads it in.
    notSent: (_refusal, reason) => {
      useLocalSavesStore.setState({ localSave: { state: 'notSent', reason } }, false, 'localSaves/saveNotSent');
      settle('failed');
    },
  });
  return outcome;
}

/**
 * Lists the saves afresh — as the save manager opens, and after a delete —
 * forgetting what went wrong before.
 */
export async function fetchLocalSaves(): Promise<void> {
  const { setLoading, setSaves, setError } = useLocalSavesStore.getState();

  setLoading(true);
  setError(null);
  useLocalSavesStore.setState({ deleteError: null }, false, 'localSaves/listing');

  try {
    const { saves } = await callRpcHttp<ListLocalSavesAnswer>('list_local_saves');
    setSaves(saves);
    console.log('[LocalSaves] Fetched', saves.length, 'local saves');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[LocalSaves] Failed to fetch local saves:', errorMessage);
    setError(errorMessage);
    // Set empty array on error to avoid null issues
    setSaves([]);
  } finally {
    setLoading(false);
  }
}

/**
 * Deletes a save, holding it as `deleting` until the list has come back
 * without it — or, failed, keeping why in `deleteError`.
 */
export async function deleteLocalSave(saveId: string): Promise<void> {
  useLocalSavesStore.setState({ deleting: saveId, deleteError: null }, false, 'localSaves/deleting');
  try {
    await callRpcHttp('delete_local_save', { save_id: saveId });
    console.log('[LocalSaves] Deleted save:', saveId);

    // Refresh the saves list
    await fetchLocalSaves();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('[LocalSaves] Failed to delete save:', errorMessage);
    useLocalSavesStore.setState({ deleteError: errorMessage }, false, 'localSaves/deleteFailed');
  } finally {
    useLocalSavesStore.setState({ deleting: null }, false, 'localSaves/deleted');
  }
}
